import {
  CloseCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Flex, Typography } from 'antd';
import { useMemo } from 'react';
import styled, { type DefaultTheme } from 'styled-components';

// Utility per controllo di tipo in switch
function checkNever(_: never) {
  return;
}

interface IWrapperBannerProps {
  type: 'warning' | 'error' | 'info';
  themeColorBg?: keyof DefaultTheme;
  themeColor?: keyof DefaultTheme;
}

export interface IBannerProps extends IWrapperBannerProps {
  icon?: React.ReactNode | null | undefined;
  content: string | React.ReactNode | null | undefined;
  showIcon?: boolean;
}

/**
 * Componente per mostrare una "fascia" informativa a piena larghezza.
 */
export function Banner(props: IBannerProps) {
  const { icon: inputIcon, content, showIcon, ...bannerProps } = props;

  const icon = useMemo(() => {
    if (!showIcon) return null;
    if (inputIcon === null) return null;
    if (inputIcon) return inputIcon;

    switch (bannerProps.type) {
      case 'error':
        return <CloseCircleOutlined aria-label="Error" />;
      case 'warning':
        return <WarningOutlined aria-label="Warning" />;
      case 'info':
        return <InfoCircleOutlined aria-label="Info" />;
      default:
        checkNever(bannerProps.type);
        return null;
    }
  }, [bannerProps.type, inputIcon, showIcon]);

  return (
    <WrapperBanner {...bannerProps}>
      <Flex align="center" justify="center" gap={12}>
        {icon && <BannerIcon>{icon}</BannerIcon>}
        <Typography.Text strong>{content}</Typography.Text>
      </Flex>
    </WrapperBanner>
  );
}

const BannerIcon = styled.span`
  font-size: 24px;
`;

const WrapperBanner = styled.div<IWrapperBannerProps>`
  min-height: 30px;
  background-color: ${props => {
    if (props.themeColorBg) {
      return props.theme[props.themeColorBg];
    }

    switch (props.type) {
      case 'error':
        return props.theme.colorErrorBg;
      case 'warning':
        return props.theme.colorWarningBg;
      case 'info':
        return props.theme.colorInfoBg;
      default:
        checkNever(props.type);
        return props.theme.colorBgBase;
    }
  }};
  color: ${props => {
    if (props.themeColor) {
      return props.theme[props.themeColor];
    }

    switch (props.type) {
      case 'error':
        return props.theme.colorError;
      case 'warning':
        return props.theme.colorWarning;
      case 'info':
        return props.theme.colorInfo;
      default:
        checkNever(props.type);
        return props.theme.colorTextBase;
    }
  }};
  padding: 12px;
`;
